"use client";

import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";

import { apiFetch } from "@/lib/api-fetch";
import { Button } from "@/components/ui/button";

/**
 * 巡检项目登记管理（② 多项目切换与管理）。
 *
 * 背景：审计确认"新增在产项目没有 CLI、没有写入口，只能手工编辑 gates/projects.json"。
 * 这里补上写入口，但把风险控制留在服务端（结构校验 + 写前备份 + 写后回读）。
 * 前端只负责：展示"哪些路径真实存在"、提交、把服务端返回的 warnings 原样显示 —— 不假装成功。
 */

interface Row {
  name: string;
  shots: string | null;
  scenes?: string | null;
  script?: string | null;
  media_dir?: string | null;
  ep?: string | null;
  jobs?: string | null;
  optional?: boolean;
  note?: string;
  exists: { shots: boolean; scenes: boolean; script: boolean; jobs: boolean };
}

const EMPTY = {
  name: "",
  shots: "",
  scenes: "",
  script: "",
  media_dir: "",
  ep: "",
  jobs: "",
  optional: true,
  note: "",
};

export function RegistryManager() {
  const t = useTranslations("pipeline");
  const router = useRouter();
  const [rows, setRows] = useState<Row[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [warns, setWarns] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [editing, setEditing] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch("/api/hermes/registry");
      if (!res.ok) throw new Error(`${res.status}`);
      const j = (await res.json()) as { projects: Row[] };
      setRows(j.projects);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async (action: "add" | "update" | "remove", originalName?: string) => {
    setBusy(true);
    setErr(null);
    setMsg(null);
    setWarns([]);
    try {
      const res = await apiFetch("/api/hermes/registry", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ action, originalName, entry: action === "remove" ? undefined : form }),
      });
      const j = (await res.json()) as { ok?: boolean; error?: string; warnings?: string[]; backup?: string };
      if (!res.ok || !j.ok) {
        setErr(j.error ?? `${res.status}`);
        return;
      }
      setMsg(`${t("saved")}${j.backup ? ` · ${t("backup")}: ${j.backup}` : ""}`);
      setWarns(j.warnings ?? []);
      setForm({ ...EMPTY });
      setEditing(null);
      await load();
      router.refresh();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (r: Row) => {
    setEditing(r.name);
    setForm({
      name: r.name,
      shots: r.shots ?? "",
      scenes: r.scenes ?? "",
      script: r.script ?? "",
      media_dir: r.media_dir ?? "",
      ep: r.ep ?? "",
      jobs: r.jobs ?? "",
      optional: Boolean(r.optional),
      note: r.note ?? "",
    });
  };

  const field = (k: keyof typeof EMPTY, label: string, ph = "") => (
    <label className="flex flex-col gap-0.5 text-[10px] text-[var(--text-muted)]">
      {label}
      <input
        value={String(form[k] ?? "")}
        placeholder={ph}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        className="h-7 rounded-md border border-[var(--border-subtle)] bg-white px-2 text-[11px] text-[var(--text-secondary)]"
      />
    </label>
  );

  return (
    <div className="space-y-2 rounded-lg border border-[var(--border-subtle)] p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-[var(--text-secondary)]">{t("registryTitle")}</span>
        <span className="text-[11px] text-[var(--text-muted)]">{rows.length}</span>
        <button
          type="button"
          className="ml-auto text-[11px] text-[var(--primary)]"
          onClick={() => {
            setOpen(!open);
            setEditing(null);
            setForm({ ...EMPTY });
          }}
        >
          {open ? t("cancel") : t("registryManage")}
        </button>
      </div>

      {msg ? <div className="text-[11px] text-[var(--success)]">{msg}</div> : null}
      {err ? <div className="text-[11px] text-[var(--destructive)]">{err}</div> : null}
      {warns.length ? (
        <ul className="space-y-0.5 rounded bg-[var(--warning)]/5 p-2 text-[11px] text-[var(--warning)]">
          {warns.map((w, i) => (
            <li key={i}>· {w}</li>
          ))}
        </ul>
      ) : null}

      {open ? (
        <div className="space-y-2 rounded-md bg-[var(--surface)] p-2">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {field("name", t("fName"), t("fNamePh"))}
            {field("shots", "shots", "…_la2_v14_ep001_inj.json")}
            {field("scenes", "scenes", "…_scenes_ep001.json")}
            {field("script", "script", "剧本.md")}
            {field("media_dir", "media_dir", "…\\成片")}
            {field("ep", "ep", "EP001")}
            {field("jobs", "jobs", "…_td_v4_jobs.json")}
            {field("note", t("fNote"))}
          </div>
          <label className="flex items-center gap-2 text-[11px] text-[var(--text-secondary)]">
            <input
              type="checkbox"
              checked={Boolean(form.optional)}
              onChange={(e) => setForm({ ...form, optional: e.target.checked })}
            />
            optional（镜头表缺失时巡检不报警）
          </label>
          <div className="flex gap-2">
            <Button
              size="sm"
              className="h-7 px-3 text-[11px]"
              disabled={busy || !form.name}
              onClick={() => void submit(editing ? "update" : "add", editing ?? undefined)}
            >
              {editing ? t("save") : t("registryAdd")}
            </Button>
            {editing ? (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-3 text-[11px]"
                onClick={() => {
                  setEditing(null);
                  setForm({ ...EMPTY });
                }}
              >
                {t("cancel")}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="space-y-1">
        {rows.map((r) => (
          <div key={r.name} className="flex flex-wrap items-center gap-2 rounded-md bg-[var(--surface)] px-2 py-1 text-[11px]">
            <span className="text-[var(--text-secondary)]">{r.name}</span>
            {(
              [
                ["shots", r.shots, r.exists.shots],
                ["scenes", r.scenes, r.exists.scenes],
                ["script", r.script, r.exists.script],
                ["jobs", r.jobs, r.exists.jobs],
              ] as [string, string | null | undefined, boolean][]
            ).map(([k, v, ok]) => (
              <span
                key={k}
                title={v ?? ""}
                className={ok ? "text-[var(--success)]" : v ? "text-[var(--destructive)]" : "text-[var(--text-muted)]"}
              >
                {k}
                {v ? (ok ? "✓" : "✗") : "–"}
              </span>
            ))}
            {r.optional ? <span className="text-[var(--text-muted)]">optional</span> : null}
            <span className="ml-auto flex gap-2">
              <button type="button" className="text-[var(--primary)]" onClick={() => startEdit(r)}>
                {t("edit")}
              </button>
              <button
                type="button"
                className="text-[var(--destructive)]"
                disabled={busy}
                onClick={() => {
                  if (window.confirm(`${t("registryRemoveConfirm")}: ${r.name}?`)) void submit("remove", r.name);
                }}
              >
                {t("registryRemove")}
              </button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
